import {
  tt,
  activeTab,
  stripIsWorthDrawing,
  tabIsVisible,
  visibleTabs,
  type HubTab,
  type TabAccess,
  type TabGuard,
} from '@agos/shared'

/**
 * The gating moved to `@agos/shared` - §9.302, and re-exported here so nothing that imports from
 * this file had to change.
 *
 * **It moved because a second hub needed it.** `/delivery-settings` became a hub too, and no module
 * imports another, so the choice was to copy five functions into `manager-delivery` or to put them
 * where both can reach them. What stays here is the tab *lists*, which are this screen's subject.
 */
export {
  activeTab,
  stripIsWorthDrawing,
  tabIsVisible,
  visibleTabs,
  type HubTab,
  type TabAccess,
  type TabGuard,
}

/**
 * Which tabs a hub shows, and to whom — FRONTEND.md §9.208.
 *
 * The owner asked for *Nastavenia* and *Preklady* to become one sidebar entry each, *"picked from
 * inside, the way Vernostný program already works"* — which is one route with a tab bar, not a
 * section with sub-items.
 *
 * **These are not one gate.** `settings_general`, `settings_sms` and `settings_notifications` are
 * held by the **waiter** in the restored data and `settings_monitoring` is not. A hub gated on all
 * five would hide it from somebody who may see three; one gated on none would offer a tab the router
 * refuses — which is FB-001's shape, and the reason this codebase has a shared access evaluator at
 * all.
 *
 * **The guards here are the routes' own**, asserted equal to them in the test beside this file. The
 * sidebar cannot offer a page the router then refuses (§9.7), and a tab is a sidebar item that has
 * moved indoors.
 */

/**
 * The screens of *Systémové nastavenia → Nastavenia*, in the owner's order.
 *
 * **Seven since §9.290**, not five: *Fakturácia* and *Franšíza* were registered routes that nothing
 * linked to, so the only way to reach them was to type the path.
 *
 * *Všeobecné* first because it is the one anybody opens by accident; the two message channels
 * together; then the routing rules; then the alerting, which is the one nobody visits twice a year.
 */
export const SETTINGS_TABS: readonly HubTab[] = [
  {
    id: 'general',
    label: tt('Všeobecné'),
    path: '/settings-general',
    guard: {
      systemRoles: ['main_admin', 'admin'],
      permission: 'settings_general',
      franchiseModule: 'settings_general',
    },
  },
  {
    // §9.290, FB-454 - the owner could not find this screen because there was no way in: a registered
    // route with no sidebar entry and no tab. Legacy hid it too, which is why the port did; what
    // changed is that the identifiers being unreachable leaves every invoice warning about them and
    // the invoice QR with no IBAN to encode.
    //
    // **No permission in the guard.** There is no `settings_billing` flag in either catalogue and no
    // role row grants one, so gating on it would lock out the admin the server admits. The route's
    // own guard is the roles, and this matches it.
    id: 'billing',
    label: tt('Fakturácia'),
    path: '/settings-billing',
    guard: { systemRoles: ['main_admin', 'admin'] },
  },
  {
    id: 'sms',
    label: tt('SMS'),
    path: '/settings-sms',
    guard: { systemRoles: ['main_admin', 'admin'], permission: 'settings_sms' },
  },
  {
    id: 'email',
    label: tt('E-mail'),
    path: '/settings-email',
    guard: { systemRoles: ['main_admin', 'admin'], permission: 'settings_email' },
  },
  {
    // §9.298 - the owner moved the push wording out of *Notifikácie & kanály* and beside *E-mail*:
    // "Move it to next to E-Mail as «Pushup»". The two pages either side own the other channels'
    // wording, so this is the third channel filed the same way.
    id: 'pushup',
    label: tt('Pushup'),
    path: '/settings-pushup',
    guard: { systemRoles: ['main_admin', 'admin'], permission: 'settings_notifications' },
  },
  {
    id: 'notifications',
    label: tt('Notifikácie & kanály'),
    path: '/settings-notifications',
    guard: { systemRoles: ['main_admin', 'admin'], permission: 'settings_notifications' },
  },
  {
    id: 'monitoring',
    label: tt('Monitoring & alerty'),
    path: '/settings-monitoring',
    guard: { systemRoles: ['main_admin', 'admin'], permission: 'settings_monitoring' },
  },
  // **`/settings-franchise` is deliberately NOT here** - §9.290 added it and the owner had it removed
  // the same day: *"if Nastavenia - Franšíza, nothing is relevant there, why its there? I think in
  // that case it can be hidden from there."*
  //
  // They are right, and the screen says so itself: *"Nič na tejto stránke neovplyvňuje to, čo
  // centrála fakturuje"* and *"Zo sekcie nie je uložené ani jedno pole"*. `franchise_settings` holds
  // `{"franchiseName": ""}`. So it was reachable from nowhere **and inert**, which is not the same
  // defect as `billing` - that one holds the identifiers every invoice needs. The route still
  // answers for a direct link, exactly as before §9.290.
]

/**
 * *Predaj a účtovanie* - the owner's item A6, §9.308.
 *
 * *"also to be moved somewhere sensible"*, said of a sidebar section that then held four items of two
 * kinds. **It holds three of one kind now**: §9.281 moved *Účty na sekeru* into *Správa Reštaurácie*,
 * where a screen a waiter runs daily belongs, and what is left is configuration.
 *
 * **All three guards are identical** - `{main_admin, admin}`, read off the routes themselves - so
 * unlike `SETTINGS_TABS` there is no per-employee narrowing to do here. An employee who sees one sees
 * all three. The gating machinery still runs; there is simply nothing for it to decide, and the test
 * beside this file asserts that rather than leaving it implied.
 *
 * Order is the owner's own: the channels first, because the other two describe what a channel
 * charges.
 */
export const SALES_TABS: readonly HubTab[] = [
  {
    id: 'channels',
    label: tt('Predajné kanály'),
    path: '/sales-channels',
    guard: { systemRoles: ['main_admin', 'admin'] },
  },
  {
    id: 'vat',
    label: tt('Sadzby DPH'),
    path: '/vat-categories',
    guard: { systemRoles: ['main_admin', 'admin'] },
  },
  {
    id: 'surcharges',
    label: tt('Príplatky a zálohy'),
    path: '/surcharges',
    guard: { systemRoles: ['main_admin', 'admin'] },
  },
]

/** *Preklady* — the settings and the editor, which legacy filed as two unrelated items. */
export const TRANSLATION_TABS: readonly HubTab[] = [
  {
    id: 'settings',
    label: tt('Nastavenia'),
    path: '/settings-ai-translate',
    guard: { permission: 'translations_admin' },
  },
  {
    id: 'editor',
    label: tt('Editor'),
    path: '/settings-translations',
    guard: { permission: 'translations_admin' },
  },
]
